import { useState, useEffect, useRef } from 'react';
import * as S from './SearchableSelect.styles';
import Input from '../input/Input';
import OutsideAlerter from '../outsidealerter/OutsideAlerter';

const RESET_VALUE = '';

type SearchableSelectProps = {
  id?: string;
  placeholder?: string;
  options?: Array<any>;
  minLength?: number;
  label?: string;
  onChange?: (e) => void;
  onlyNumbers?: boolean;
  onClick?: (e) => void;
  disabled?: boolean;
  inputStatus?: 'normal' | 'error';
  message?: string;
  pattern?: string;
  onKeyDown?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  name?: string;
  maxLength?: number;
  value?: any;
  showResetButton?: boolean;
};

const SearchableSelect = (props: SearchableSelectProps) => {
  const {
    id,
    placeholder,
    options,
    minLength,
    label,
    onChange,
    onlyNumbers,
    onClick,
    disabled,
    inputStatus,
    message,
    pattern,
    onKeyDown,
    onFocus,
    onBlur,
    name,
    maxLength,
  } = props;
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showResetButton, setShowResetButton] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [isWrite, setIsWrite] = useState(false);
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [showPlaceHolder, setShowPlaceHolder] = useState(!!placeholder);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open || isWrite) inputRef.current.focus();
  }, [open, isWrite]);

  useEffect(() => {
    filteredOptions !== options && setFilteredOptions(options || []);
  }, [options]);

  useEffect(() => {
    const isOpen = !selectedOption && filteredOptions.length !== 0 && isWrite;
    minLength ? minLength <= value.length && setOpen(isOpen) : setOpen(isOpen);
  }, [filteredOptions, isWrite]);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setValue(props.value || '');
  }, [props.value]);

  const handleOnChange = (event) => {
    const searchWord = event.target.value;
    const isValidValue = validateValue(searchWord);
    setIsWrite(true);
    setShowResetButton(false);
    setSelectedOption(RESET_VALUE);
    const newFilteredOptions = options?.filter((item) =>
      item?.label.toLowerCase().includes(searchWord.toLowerCase())
    );
    if (newFilteredOptions) {
      setFilteredOptions(newFilteredOptions || []);
    } else {
      setSelectedOption(RESET_VALUE);
      setFilteredOptions(options || []);
    }
    if (isValidValue) {
      setValue(searchWord);
      minLength
        ? minLength <= searchWord.length
          ? onChange(searchWord)
          : handleClose()
        : onChange(searchWord);
    }
  };

  const validateValue = (searchWord: string) => {
    if (onlyNumbers || pattern) {
      const regex = onlyNumbers ? /^[0-9]{0,}$/ : /([A-Z])\w+/;
      return regex.test(searchWord);
    } else return true;
  };

  const handleReset = () => {
    onChange && onChange('');
    setValue(RESET_VALUE);
    setSelectedOption(RESET_VALUE);
    setShowResetButton(false);
    setIsWrite(false);
    setShowPlaceHolder(true);
    handleClose();
  };

  const handleOnClick = (option) => {
    const { value = '', code = '' } = option;
    const selectedValue = code || value;
    onClick && onClick(option);
    setIsWrite(false);
    setValue(selectedValue);
    setSelectedOption(selectedValue);
    setShowResetButton(true);
    onChange && onChange(selectedValue);
    handleClose();
  };

  const handleOnFocus = () => {
    onFocus && onFocus();
    setShowPlaceHolder(false);
  };
  const handleOnBlur = () => {
    onBlur && onBlur();
    setShowPlaceHolder(value.length == 0);
  };

  const renderFilteredOptions = () => (
    <S.DataResultWrapper>
      <S.DataResult>
        {filteredOptions.map((filteredOption, key) => (
          <S.DataItem key={key} onClick={() => handleOnClick(filteredOption)}>
            <S.DataItemText>{filteredOption.label}</S.DataItemText>
          </S.DataItem>
        ))}
      </S.DataResult>
    </S.DataResultWrapper>
  );

  return (
    <OutsideAlerter handleOutSideClick={handleReset} active={open}>
      <S.SearchableSelectWrapper data-testid='searchable-select'>
        <Input
          disabled={disabled}
          inputRef={inputRef}
          id={id}
          type='text'
          label={label}
          placeHolder={showPlaceHolder ? placeholder : ''}
          onChange={handleOnChange}
          value={value}
          autocomplete='off'
          icons={{
            iconLeft: 'search_outline',
          }}
          inputStatus={inputStatus}
          message={!showResetButton ? message : ''}
          pattern={pattern}
          onKeydown={onKeyDown}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          name={name}
          maxLength={maxLength}
        />
        {open && renderFilteredOptions()}
      </S.SearchableSelectWrapper>
    </OutsideAlerter>
  );
};

SearchableSelect.defaultProps = {
  options: [],
  placeholder: '',
  label: '',
  disabled: false,
  inputStatus: 'normal',
};

export default SearchableSelect;
